import React, { useState } from 'react'
import { AxiosError } from 'axios';
import AdminLayout from '../../../layouts/AdminLayout'
import { Accordion, useAccordionButton } from 'react-bootstrap'
import { IconAdjustmentsHorizontal, IconHome, IconMapPin, IconCalendar, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import SearchProperties from '../components/SearchProperties';
import useIndexProperties from '../../../services/properties/hooks/useIndexProperties';
import FilterComponent from '../components/property-list/FilterComponent';
import { IndexFilter } from '../../../services/properties/schema/IndexFilterSchema';
import PropertiesTable from '../components/property-list/PropertiesTable';
import BankAccountDialog from '../components/BankAccountDialog';
import CreateBankAccountDialog from '../components/CreateBankAccountDialog';
import UpdateBankAccountDialog from '../components/UpdateBankAccountDialog';
import CreatePropertyDialog from '../components/CreatePropertyDialog';
import UpdatePropertyDialog from '../components/UpdatePropertyDialog';
import AssignUserToProperty from '../components/AssignUserToProperty';
import { PropertyResponseType } from '../../../services/properties/response/IndexPropertiesResponse';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useRemoveProperties } from '../../../services/properties/hooks/useRemoveProperties';
import { useTogglePropertyActive } from '../../../services/properties/hooks/useTogglePropertyActive';
import ActivationProgressModal from '../components/ActivationProgressModal';
import DeactivatePropertyDialog from '../components/DeactivatePropertyDialog';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import PaginationComponent from '../components/PaginationComponent';
import usePropertyStatistics from '../../../services/statistics/hooks/usePropertyStatistics';
import usePropertyRegionStatistics from '../../../services/statistics/hooks/usePropertyRegionStatistics';
import StatisticsList from '../components/StatisticsList';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function CustomToggle({ children, eventKey }: { children: React.ReactNode, eventKey: string }) {
    const decoratedOnClick = useAccordionButton(eventKey);

    return (
        <button className="btn btn-icon me-2" onClick={decoratedOnClick}>
            {children}
        </button>
    );
}

const PropertyManagementPage: React.FC = () => {
    const [search, setSearch] = useState<string>('');
    const [propertyType, setPropertyType] = useState<string | null>('');
    const [preferredCurrency, setPreferredCurrency] = useState<string | null>('');
    const [isPropertyActiveFilter, setIsPropertyActiveFilter] = useState<string | null>('');
    const [needsReviewFilter, setNeedsReviewFilter] = useState<string | null>('');
    const [groupFilter, setGroupFilter] = useState<string | null>('');
    const [billingConnectedFilter, setBillingConnectedFilter] = useState<string | null>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const { data: properties, isFetching, isError, isSuccess } = useIndexProperties({
        limit: perPage,
        page: currentPage,
        filter: {
            property_type: propertyType ?? undefined,
            preferred_currency: preferredCurrency ?? undefined,
            is_property_active: isPropertyActiveFilter ?? undefined,
            needs_review: needsReviewFilter ?? undefined,
            group_id: groupFilter ?? undefined,
            billing_connected: billingConnectedFilter ?? undefined,
        },
        search: search,
        include: 'groups'
    });

    const { data: statistics, isLoading: isStatisticsLoading, isError: isStatisticsError } = usePropertyStatistics();
    const { data: regionStatistics, isLoading: isRegionStatisticsLoading, isError: isRegionStatisticsError } = usePropertyRegionStatistics();
    const [showStatistics, setShowStatistics] = useState<boolean>(false);

    const handleSearch = (query: string) => {
        setSearch(query);
    }
    const handleFilter = (data: IndexFilter) => {
        setPropertyType(data.property_type ?? null);
        setPreferredCurrency(data.preferred_currency ?? null);
        setIsPropertyActiveFilter(data.is_property_active ?? null);
        setNeedsReviewFilter(data.needs_review ?? null);
        setGroupFilter(data.group_id ?? null);
        setBillingConnectedFilter(data.billing_connected ?? null);
        // Hasil filter bisa lebih sedikit dari halaman saat ini -> kembali ke halaman 1.
        setCurrentPage(1);
    }

    const [showBankAccounts, setShowBankAccounts] = useState<boolean>(false);
    const [selectedPropertyForBankAccounts, setSelectedPropertyForBankAccounts] = useState<string | null>(null);
    const [showCreateBankAccount, setShowCreateBankAccount] = useState<boolean>(false);
    const [showUpdateBankAccount, setShowUpdateBankAccount] = useState<boolean>(false);
    const [selectedBankAccount, setSelectedBankAccount] = useState<string | null>(null);

    const handleOpenBankAccounts = (property_id: string) => {
        setSelectedPropertyForBankAccounts(property_id);
        setShowBankAccounts(true);
    };

    const handleCloseBankAccounts = () => {
        setSelectedPropertyForBankAccounts('');
        setShowBankAccounts(false);
    };

    const handleOpenBankAccountsCreate = () => {
        setShowCreateBankAccount(true);
    };

    const handleOpenUpdateBankAccount = (bankId: string) => {
        setSelectedBankAccount(bankId);
        setShowUpdateBankAccount(true);
    };

    const handleCloseUpdateBankAccount = () => {
        setSelectedBankAccount('');
        setShowUpdateBankAccount(false);
    };

    const [showCreate, setShowCreate] = useState<boolean>(false);
    const [showUpdate, setShowUpdate] = useState<boolean>(false);
    const [idWillUpdate, setIdWillUpdate] = useState<string | null>(null);
    const [showAssignUser, setShowAssignUser] = useState<boolean>(false);
    const [selectedPropertyToAssign, setSelectedPropertyToAssign] = useState<string | null>(null);

    const handleOpenUpdate = (id: string) => {
        setShowUpdate(true);
        setIdWillUpdate(id);
    }

    const handleCloseUpdate = () => {
        setShowUpdate(false);
        setIdWillUpdate(null);
    }

    const handleOpenAssignUser = (property_id: string) => {
        setSelectedPropertyToAssign(property_id);
        setShowAssignUser(true);
    };

    const handleCloseAssignUser = () => {
        setSelectedPropertyToAssign('');
        setShowAssignUser(false);
    };

    const { mutate } = useRemoveProperties();

    const queryClient = useQueryClient();

    const handleRemoveProperty = (property: PropertyResponseType) => {
        toast.loading('Removing property...', { id: 'remove-property' });

        mutate({ id: property.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['properties'].includes(key as string))
                });

                toast.success('Property removed successfully', { id: 'remove-property' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove property', { id: 'remove-property' });
            }
        });
    }

    // --- Activate / deactivate on Channex ---
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [activation, setActivation] = useState<{
        activationId: string | null;
        propertyId: string;
        propertyName: string;
        direction: 'activate' | 'deactivate';
    } | null>(null);
    const { mutate: toggleActive } = useTogglePropertyActive();

    const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; name: string } | null>(null);

    const runToggleActive = (property: { id: string; name: string }, next: boolean, unassignUserIds: string[] = []) => {
        setTogglingId(property.id);
        toggleActive({ id: property.id, is_active: next, unassign_user_ids: unassignUserIds }, {
            onSuccess: (res) => {
                if (!res.data.activation_id) {
                    // Already in the requested state — no job queued, just refresh the list.
                    queryClient.refetchQueries({
                        predicate: (query) => query.queryKey.some((key) => ['properties'].includes(key as string))
                    });
                    setTogglingId(null);
                    return;
                }
                setActivation({
                    activationId: res.data.activation_id,
                    propertyId: property.id,
                    propertyName: property.name,
                    direction: next ? 'activate' : 'deactivate',
                });
            },
            onError: (mutationError: Error) => {
                const axiosError = mutationError as AxiosError<{ message?: string }>;
                const message = axiosError?.response?.data?.message || axiosError?.message || 'Failed to update property status';
                toast.error(message);
                setTogglingId(null);
            }
        });
    };

    const handleToggleActive = (property: { id: string; name: string }, next: boolean) => {
        if (!next) {
            // Deactivate is destructive to Channex mapping — confirm first (dialog also offers
            // un-assigning users from the property).
            setDeactivateTarget(property);
            return;
        }
        runToggleActive(property, next);
    };

    const handleActivationFinished = () => {
        setTogglingId(null);
        queryClient.refetchQueries({
            predicate: (query) => query.queryKey.some((key) => ['properties'].includes(key as string))
        });
    };

    const handleCloseActivationModal = () => {
        setActivation(null);
        setTogglingId(null);
    };

    const handleRetryActivation = () => {
        if (!activation) return;
        const nextActive = activation.direction === 'activate';
        setActivation(null);
        runToggleActive({ id: activation.propertyId, name: activation.propertyName }, nextActive);
    };

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container min-vh-100">
                    <Accordion defaultActiveKey="0" flush>
                        <div className="search-container mb-3">
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <CustomToggle eventKey="0">
                                    <IconAdjustmentsHorizontal className="text-secondary icon" />
                                </CustomToggle>
                                <div className="me-2">
                                    <select
                                        className="form-select form-control"
                                        value={perPage} // Menetapkan nilai default
                                        onChange={(e) => {
                                            setPerPage(Number(e.target.value)); // Mengubah jumlah item per halaman
                                            setCurrentPage(1); // Reset ke halaman pertama setiap kali opsi diubah
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                                {/* Tambahkan flex-grow-1 agar search mengambil sisa ruang */}
                                <div className="flex-grow-1 me-2">
                                    <SearchProperties onSearch={handleSearch} />
                                </div>
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_create']} asComponent>
                                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create</button>
                                </RequireAuth>
                            </div>
                        </div>
                        <Accordion.Collapse eventKey="0">
                            <FilterComponent handleFilter={handleFilter} />
                        </Accordion.Collapse>
                    </Accordion>

                    {/* Statistics Section */}
                    {isStatisticsLoading && (
                        <div className="text-center my-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading statistics...</span>
                            </div>
                        </div>
                    )}

                    {isStatisticsError && (
                        <div className="alert alert-danger my-4 d-flex align-items-center">
                            <IconAdjustmentsHorizontal className="me-2" />
                            <span>Failed to load statistics.</span>
                        </div>
                    )}

                    {/* Always-visible summary row — sits below the filter, outside the
                        collapsible Property Overview panel, so the key counts don't
                        require an extra click to see. */}
                    {!isStatisticsLoading && !isStatisticsError && statistics && (
                        <div className="g-4 mb-4">
                            <div className="d-flex align-items-center justify-content-between flex-wrap h-100 p-3 rounded bg-light">
                                <div className="d-flex align-items-center mb-2 mb-md-0 gap-4">
                                    <div>
                                        <div className="text-uppercase text-muted small fw-semibold">Total Properties</div>
                                        <div className="h3 mb-0 text-primary fw-bold">{statistics.data.total.total_property}</div>
                                    </div>
                                    {Object.entries(statistics.data.total.billing_type).map(([key, value]) => {
                                        const formattedKey = key
                                            .split('_')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                            .join(' ');
                                        return (
                                            <div key={key}>
                                                <div className="text-uppercase text-muted small fw-semibold">{formattedKey}</div>
                                                <div className="h3 mb-0 text-primary fw-bold">{value}</div>
                                            </div>
                                        );
                                    })}
                                    {statistics.data.total.total_room_vacation_rental !== undefined && (
                                        <div>
                                            <div className="text-uppercase text-muted small fw-semibold">Total Room (Vacation Rental)</div>
                                            <div className="h3 mb-0 text-primary fw-bold">{statistics.data.total.total_room_vacation_rental}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="text-muted small">
                                    Showing totals for active properties only, {new Date().getFullYear()}
                                </div>
                            </div>
                        </div>
                    )}

                    {!isStatisticsLoading && !isStatisticsError && statistics && (
                        <div className="card shadow-sm mb-4">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <IconHome className="me-2 text-primary" />
                                    <h5 className="mb-0">Property Overview</h5>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <small className="text-muted">
                                        Last updated: {new Date().toLocaleDateString()}
                                    </small>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light border-0 d-flex align-items-center"
                                        onClick={() => setShowStatistics((prev) => !prev)}
                                        aria-label={showStatistics ? 'Hide statistics' : 'Show statistics'}
                                    >
                                        {showStatistics ? (
                                            <IconChevronUp size={18} />
                                        ) : (
                                            <IconChevronDown size={18} />
                                        )}
                                    </button>
                                </div>
                            </div>
                            {showStatistics && (
                            <div className="card-body">
                                {/* Details: chart + distributions */}
                                <div className="row g-4">
                                    {/* Monthly creation chart */}
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-3">
                                                    <IconCalendar className="me-2 text-primary" />
                                                    <h6 className="mb-0 text-uppercase text-muted small">Monthly Property Creation</h6>
                                                </div>
                                                <div style={{ width: '100%', height: 260 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={statistics.data.monthly_property_creation}>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                            <XAxis
                                                                dataKey="month"
                                                                tickFormatter={(value: number | string) => {
                                                                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                                    const numericValue = typeof value === 'number' ? value : Number(value);
                                                                    const index = numericValue - 1;
                                                                    return monthNames[index] ?? value;
                                                                }}
                                                            />
                                                            <YAxis allowDecimals={false} />
                                                            <Tooltip />
                                                            <Bar dataKey="total" radius={[4, 4, 0, 0]} fill="#0d6efd" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Property types */}
                                    <div className="col-md-6 col-lg-4">
                                        <StatisticsList
                                            title="Property Types"
                                            icon={<IconMapPin className="me-2 text-success" />}
                                            data={statistics.data.total.property_type}
                                            badgeClassName="badge bg-success-subtle text-success"
                                            emptyMessage="No property type data available."
                                        />
                                    </div>

                                    {/* City distribution */}
                                    <div className="col-md-6 col-lg-4">
                                        <StatisticsList
                                            title="City Distribution"
                                            icon={<IconMapPin className="me-2 text-info" />}
                                            data={statistics.data.city_distribution}
                                            badgeClassName="badge bg-info-subtle text-info"
                                            emptyMessage="No city data available."
                                        />
                                    </div>

                                    {/* Region distribution */}
                                    <div className="col-md-6 col-lg-4">
                                        {isRegionStatisticsLoading ? (
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                                                    <div className="spinner-border text-primary spinner-border-sm mb-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <span className="text-muted small">Loading region statistics...</span>
                                                </div>
                                            </div>
                                        ) : isRegionStatisticsError || !regionStatistics ? (
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-4" style={{ minHeight: '200px' }}>
                                                    <span className="text-danger small mb-2">Failed to load region statistics.</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <StatisticsList
                                                title="Region Distribution"
                                                icon={<IconMapPin className="me-2 text-primary" />}
                                                data={regionStatistics.data}
                                                badgeClassName="badge bg-primary-subtle text-primary"
                                                emptyMessage="No region data available."
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}

                    <PropertiesTable
                        properties={properties ?? { success: false, message: '', code: 0, data: [] }}
                        onUpdate={handleOpenUpdate}
                        onAssign={handleOpenAssignUser}
                        onBankAccounts={handleOpenBankAccounts}
                        onRemove={handleRemoveProperty}
                        onToggleActive={handleToggleActive}
                        togglingId={togglingId}
                        isFetching={isFetching}
                        isError={isError}
                        isSuccess={isSuccess}
                        onPageChange={setCurrentPage}
                        currentPage={currentPage}
                    />
                    <PaginationComponent
                        totalPages={properties?.pagination?.last_page ?? 1}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                    {/* <Footer /> */}
                </div>
            </div>
            {selectedPropertyForBankAccounts && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_view']} asComponent>
                    <BankAccountDialog show={showBankAccounts} onClose={handleCloseBankAccounts} propertyId={selectedPropertyForBankAccounts} onCreate={handleOpenBankAccountsCreate} onUpdate={handleOpenUpdateBankAccount} />
                </RequireAuth>
            )}
            {selectedPropertyForBankAccounts && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_create']} asComponent>
                    <CreateBankAccountDialog show={showCreateBankAccount} onClose={() => setShowCreateBankAccount(false)} propertyId={selectedPropertyForBankAccounts} />
                </RequireAuth>
            )}
            {selectedPropertyForBankAccounts && selectedBankAccount && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_update']} asComponent>
                    <UpdateBankAccountDialog show={showUpdateBankAccount} onClose={handleCloseUpdateBankAccount} bankId={selectedBankAccount} />
                </RequireAuth>
            )}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_create']} asComponent>
                <CreatePropertyDialog show={showCreate} onClose={() => setShowCreate(false)} />
            </RequireAuth>
            {idWillUpdate && showUpdate && <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_update']} asComponent><UpdatePropertyDialog show={showUpdate} onClose={handleCloseUpdate} selectedProperty={idWillUpdate} /></RequireAuth>}
            {selectedPropertyToAssign && showAssignUser && <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_assign_user']} asComponent><AssignUserToProperty show={showAssignUser} onClose={handleCloseAssignUser} selectedPropertyToAssign={selectedPropertyToAssign} /></RequireAuth>}
            <DeactivatePropertyDialog
                show={!!deactivateTarget}
                property={deactivateTarget}
                onCancel={() => setDeactivateTarget(null)}
                onConfirm={(unassignUserIds) => {
                    if (!deactivateTarget) return;
                    const target = deactivateTarget;
                    setDeactivateTarget(null);
                    runToggleActive(target, false, unassignUserIds);
                }}
            />
            <ActivationProgressModal
                show={!!activation}
                propertyId={activation?.propertyId ?? null}
                propertyName={activation?.propertyName}
                activationId={activation?.activationId ?? null}
                direction={activation?.direction ?? null}
                onClose={handleCloseActivationModal}
                onFinished={handleActivationFinished}
                onRetry={handleRetryActivation}
            />
        </AdminLayout>
    )
}

export default PropertyManagementPage